import { CountryCode } from "@vygruppen/vy-train-map";
import { withErrorBoundary } from "app/ErrorBoundary/ErrorBoundaryDashboard";
import {
  SharedContentGridLayout,
  SharedGridStyle,
} from "features/CenterContent/shared/MainContentGrid";
import TabsCenter from "features/CenterContent/shared/Tabs/TabsCenter";
import { StickyController } from "features/CenterContent/VehicleDetails/shared/StickyController";
import TrainComposition from "features/CenterContent/VehicleDetails/TrainDetails/TrainComposition/TrainComposition";
import { ContentGridBreakpoint } from "features/CenterContent/VehicleDetails/TrainDetails/TrainComposition/TrainSummary/TrainSummary";
import TrainCondition from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/TrainCondition";
import { TrainGPS } from "features/CenterContent/VehicleDetails/TrainDetails/TrainGPS/TrainGPS";
import TrainRoute from "features/CenterContent/VehicleDetails/TrainDetails/TrainRoute/TrainRoute";
import { getParametersForNorwegianTrainInSweden } from "features/CenterContent/VehicleDetails/TrainDetails/TrainStaff/getParametersForNorwegianTrainInSweden";
import TrainStaff from "features/CenterContent/VehicleDetails/TrainDetails/TrainStaff/TrainStaff";
import { useTrainRouteWithPeriodicRefetch } from "features/CenterContent/VehicleDetails/TrainDetails/useTrainRoute";
import { useParams } from "react-router-dom";
import { makeClamp } from "shared/components/CollapsibleWrapper";
import { Box } from "shared/components/Spor";
import { hideOnWidthClassname } from "shared/utils/hideOnWidth";
import styled, { useTheme } from "styled-components";

const ContentGrid = styled.div`
  ${SharedContentGridLayout};
  margin: 0;
  overflow: hidden;

  grid-template-rows: 9rem 1fr auto;
  grid-template-columns: ${makeClamp()} 1fr ${makeClamp()};
  grid-template-areas:
    "train-composition train-composition train-composition"
    "train-route train-condition train-staff"
    "train-route train-condition train-gps";

  & .sweden {
    grid-column-end: span 2;
    grid-row-end: span 2;
  }

  /* DashboardLayout.tsx is the container */
  @container (max-width: ${ContentGridBreakpoint}px) {
    grid-template-rows: auto 1fr;
    grid-template-columns: 1fr 1fr;
    grid-template-areas:
      "train-composition train-composition"
      "train-route train-details";
  }
`;

const TrainRouteArea = styled.div`
  /* SharedGridStyle is applied to HeadedContainer instead */
  grid-area: train-route;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0px;
  overflow-y: auto;
`;

const TrainCompositionArea = styled.div`
  ${SharedGridStyle};
  grid-area: train-composition;
  display: flex;
  flex-direction: row;
  min-width: 220px;
  align-items: center;
  justify-content: space-between;
  padding: 12px;
  gap: 0;

  @container (max-width: ${ContentGridBreakpoint}px) {
    /* Only change styling if there is non-error content */
    &:has(> :nth-child(2)) {
      flex-direction: column;
      gap: 9px;
      padding: 0;
      background: transparent;
      border: none;

      & > * {
        background: ${({ theme }) => theme.colorBackgroundMain};
        border-radius: 6px;
        padding: 18px;
        border: 1px solid ${({ theme }) => theme.colorOutlineSecondary};
        width: 100%;
      }
    }
  }
`;

const TrainConditionArea = styled(Box)`
  /* SharedGridStyle is applied to HeadedContainer instead */
  grid-area: train-condition;
  overflow-y: auto;
`;

const TrainGPSArea = styled.div`
  aspect-ratio: 16/9;
  max-height: 100%;
  min-width: 100%;
  ${SharedGridStyle};
  grid-area: train-gps;
  padding: 0;
`;

const TrainStaffArea = styled.div`
  /* SharedGridStyle is applied to HeadedContainer instead */
  grid-area: train-staff;
  overflow-y: auto;
`;

const TrainDetailsArea = styled.div`
  grid-area: train-details;
  overflow-y: auto;
`;

export type DetailsParams = {
  trainNumber?: string;
  trainDate?: string;
  countryCode?: CountryCode;
};

const TrainDetails = () => {
  const theme = useTheme();
  const { trainNumber, trainDate, countryCode } = useParams<DetailsParams>();
  const trainRoute = useTrainRouteWithPeriodicRefetch({
    identifier: trainNumber,
    nominalDate: trainDate,
    countryCode,
  });

  const {
    isNorwegianTrainInSweden,
    norwegianTrainStaffCountryCode,
    norwegianTrainId,
  } = getParametersForNorwegianTrainInSweden(trainRoute.data);

  const hideOnSmall = hideOnWidthClassname(
    "hide-on-small",
    ContentGridBreakpoint,
  );
  const hideOnLarge = hideOnWidthClassname(
    "hide-on-large",
    ContentGridBreakpoint,
  );

  return (
    <>
      <TabsCenter />
      <ContentGrid>
        <TrainCompositionArea>
          <TrainComposition
            nominalDate={trainDate}
            trainId={trainNumber}
            trainRoute={trainRoute}
            countryCode={countryCode}
          />
        </TrainCompositionArea>

        <TrainRouteArea>
          <TrainRoute
            trainNumber={trainNumber}
            nominalDate={trainDate}
            countryCode={countryCode}
            trainRoute={trainRoute}
          />
        </TrainRouteArea>

        {countryCode === "NO" && (
          <>
            {/* On smaller screens we combine into two areas */}
            <TrainDetailsArea className={hideOnLarge}>
              <StickyController
                stickyBackground={theme.colorBackgroundBase}
                headerHeight="54px"
                gap="6px"
              >
                <TrainCondition />
                <TrainStaff
                  nominalDate={trainDate}
                  trainId={trainNumber}
                  countryCode={countryCode}
                />
                <TrainGPS sticky />
              </StickyController>
            </TrainDetailsArea>

            {/* On larger screens we divide into three areas */}
            <TrainConditionArea className={hideOnSmall}>
              <StickyController
                stickyBackground={theme.colorBackgroundBase}
                headerHeight="54px"
                gap="6px"
              >
                <TrainCondition />
              </StickyController>
            </TrainConditionArea>
            <TrainStaffArea className={hideOnSmall}>
              <StickyController
                stickyBackground={theme.colorBackgroundBase}
                headerHeight="54px"
                gap="6px"
              >
                <TrainStaff
                  nominalDate={trainDate}
                  trainId={trainNumber}
                  countryCode={countryCode}
                />
              </StickyController>
            </TrainStaffArea>
            <TrainGPSArea className={hideOnSmall}>
              <TrainGPS />
            </TrainGPSArea>
          </>
        )}

        {countryCode === "SE" && (
          <>
            <TrainDetailsArea className={hideOnLarge}>
              <StickyController
                stickyBackground={theme.colorBackgroundBase}
                headerHeight="54px"
                gap="6px"
              >
                <TrainCondition />
                {isNorwegianTrainInSweden && (
                  <TrainStaff
                    nominalDate={trainDate}
                    trainId={norwegianTrainId}
                    countryCode={norwegianTrainStaffCountryCode}
                    isNorwegianTrainInSweden
                  />
                )}
              </StickyController>
            </TrainDetailsArea>

            <TrainConditionArea
              className={hideOnSmall}
              gridColumn={isNorwegianTrainInSweden ? undefined : "2 / -1"}
            >
              <StickyController
                stickyBackground={theme.colorBackgroundBase}
                headerHeight="54px"
                gap="6px"
              >
                <TrainCondition />
              </StickyController>
            </TrainConditionArea>
            {isNorwegianTrainInSweden && (
              <TrainStaffArea className={hideOnSmall}>
                <StickyController
                  stickyBackground={theme.colorBackgroundBase}
                  headerHeight="54px"
                  gap="6px"
                >
                  <TrainStaff
                    nominalDate={trainDate}
                    trainId={norwegianTrainId}
                    countryCode={norwegianTrainStaffCountryCode}
                    isNorwegianTrainInSweden
                  />
                </StickyController>
              </TrainStaffArea>
            )}
          </>
        )}
      </ContentGrid>
    </>
  );
};

export default withErrorBoundary(TrainDetails);
